import { Flex, StackScreen, Typography, useScreenSize } from '@banhala/ui-kit';
import { useQueryClient } from '@tanstack/react-query';
import { queryKey as myPageQueryKey } from 'mypage/src/constant/queryKey';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { TabView } from 'react-native-tab-view';
import { CartIcon, SearchIcon } from 'shared/src/component';
import { queryKey as sharedQueryKey } from 'shared/src/constant';
import { AnalyticsScreen } from 'shared/src/context';
import { useHandleBackPressed, useReportTTFD } from 'shared/src/hooks';
import {
  myPersonalizationTabRoutes,
  type MyPersonalizationTabRoute,
} from '../../constant/tab';
import { useMyPageRoute } from '../../hooks/useMyPageRoute';
import MyPersonalizationBodyShapeBeautyContent from './components/MyPersonalizationBodyShapeBeautyContent';
import MyPersonalizationSizeContent from './components/MyPersonalizationSizeContent';
import { MyPersonalizationTabBar } from './components/MyPersonalizationTabBar';

const MyPersonalization = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const queryClient = useQueryClient();

  const routes = useMemo(() => myPersonalizationTabRoutes, []);
  const size = useScreenSize();
  const route = useMyPageRoute<'MyPersonalization'>();
  const { params } = route;

  const [index, setIndex] = useState<number>(
    Number.isFinite(params?.initialTabIndex)
      ? Number(params?.initialTabIndex)
      : 0,
  );

  const handleIndexChange = useCallback((index: number) => {
    setIndex(index);
  }, []);

  useReportTTFD();

  // 진입 시 캐시 자체를 제거. useEffect로는 children Suspense 쿼리가
  // stale 캐시를 먼저 읽어 form hydration이 옛 값으로 락되는 race가 있음.
  // useState lazy initializer는 render 단계에서 children 마운트 직전에 실행되어
  // useSuspenseQuery가 비어있는 캐시를 보고 재fetch하도록 보장.
  useState(() => {
    queryClient.resetQueries({ queryKey: sharedQueryKey.myInfo });
    queryClient.resetQueries({ queryKey: myPageQueryKey.beautyInfo() });
    return null;
  });

  return (
    <AnalyticsScreen screen="MY_PERSONALIZATION">
      <StackScreen
        title="맞춤 정보"
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        rightIcons={[<SearchIcon key="search" />, <CartIcon key="cart" />]}
      >
        <TabView<MyPersonalizationTabRoute>
          lazy={true}
          navigationState={{ index, routes }}
          renderScene={item => {
            if (item.route.type === 'CUSTOM') {
              if (item.route.screen_name === 'BODY_SHAPE_AND_BEAUTY')
                return (
                  <Suspense
                    fallback={
                      <Flex.Center flex={1}>
                        <ActivityIndicator />
                      </Flex.Center>
                    }
                  >
                    <MyPersonalizationBodyShapeBeautyContent
                      onClose={onPressBack}
                    />
                  </Suspense>
                );
              if (item.route.screen_name === 'SIZE') {
                return (
                  <Suspense
                    fallback={
                      <Flex.Center flex={1}>
                        <ActivityIndicator />
                      </Flex.Center>
                    }
                  >
                    <MyPersonalizationSizeContent onClose={onPressBack} />
                  </Suspense>
                );
              }
            }

            return <Typography>{item.route.name}</Typography>;
          }}
          onIndexChange={handleIndexChange}
          initialLayout={{
            width: size.width,
          }}
          renderTabBar={props => <MyPersonalizationTabBar {...props} />}
        />
      </StackScreen>
    </AnalyticsScreen>
  );
};

export default MyPersonalization;
